import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { GraduationCap, Users, Send, RefreshCw, Loader2, Clock, Trophy, Sparkles, Upload, UserPlus, Trash2, X, Search, Pencil, Plus, MessageSquareText } from "lucide-react";
import { toast } from "sonner";
import TemplatePicker from "@/components/TemplatePicker";
import SegmentMeter from "@/components/SegmentMeter";
import { SyncHistoryDialog } from "@/components/SyncHistoryDialog";

function ClassCard({ cls, onOpen, onBroadcast, onEdit }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 hover:shadow-sm transition-shadow cursor-pointer group relative" onClick={() => onOpen(cls)} data-testid={`class-card-${cls.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
          <GraduationCap className="w-5 h-5 text-[#D9232E]" />
        </div>
        <div className="flex items-center gap-1">
          {cls.division && cls.division !== "Other" && (
            <Badge variant="outline" className="text-[10px] font-semibold border-slate-300">{cls.division}</Badge>
          )}
          {cls.location_code && (
            <Badge variant="outline" className="text-[10px] font-mono font-semibold border-slate-300">
              {cls.location_code === "Victoria" ? "VIC" : cls.location_code}
            </Badge>
          )}
          <Badge variant="secondary" className="flex items-center gap-1 text-[10px]">
            <Users className="w-3 h-3" /> {cls.enrolled_count}
          </Badge>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onEdit(cls); }}
            className="p-1 text-slate-400 hover:text-[#D9232E] hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid={`class-edit-${cls.id}`}
            title="Edit class"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <h3 className="font-bold text-slate-900 text-[15px] leading-tight">{cls.name}</h3>
      <p className="text-xs text-slate-500 mt-1">{cls.instructor}</p>
      <div className="text-xs text-slate-500 mt-3 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
        <span className="flex items-center gap-2 text-slate-500"><Clock className="w-3 h-3" /> {cls.day}s · {cls.time}</span>
        <Button
          size="sm"
          onClick={(e) => { e.stopPropagation(); onBroadcast(cls); }}
          disabled={!cls.enrolled_count}
          className="h-7 px-2.5 bg-[#D9232E] hover:bg-[#B91C25] text-white text-[11px] font-semibold disabled:opacity-40"
          data-testid={`class-quick-send-${cls.id}`}
          title={cls.enrolled_count ? `Text all ${cls.enrolled_count} families in this class` : "No students enrolled"}
        >
          <Send className="w-3 h-3 mr-1" /> Text class
        </Button>
      </div>
    </div>
  );
}

export default function Classes() {
  const [summary, setSummary] = useState(null);
  const [classes, setClasses] = useState([]);
  const [tab, setTab] = useState("Recreational");
  const [compDiv, setCompDiv] = useState("All");
  const [active, setActive] = useState(null);
  const [students, setStudents] = useState([]);
  const [openMsg, setOpenMsg] = useState(false);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [broadcastMode, setBroadcastMode] = useState(null); // { program, division, label, count }
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncText, setSyncText] = useState("");
  const [syncFileName, setSyncFileName] = useState("");
  const [syncPreview, setSyncPreview] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncApplying, setSyncApplying] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ mode: "existing", student_id: "", phone: "", student_name: "", parent_first_name: "" });
  const [addSaving, setAddSaving] = useState(false);
  const [allStudents, setAllStudents] = useState([]);
  const [studentSearch, setStudentSearch] = useState("");
  const [classSearch, setClassSearch] = useState("");
  const [classDay, setClassDay] = useState("All");
  const [searchParams, setSearchParams] = useSearchParams();
  const nav = useNavigate();

  // Cache classes list across tab switches — Classes tab is opened often after Inbox
  const cachedClassList = (() => { try { return JSON.parse(sessionStorage.getItem(`sts:classes:${tab}:${compDiv}`) || "null") || null; } catch { return null; } })();
  const loadSummary = async () => {
    try { const { data } = await api.get("/programs/summary"); setSummary(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const loadClasses = async () => {
    const params = {};
    if (tab === "Competitive") {
      params.program = "Competitive";
      if (compDiv !== "All") params.division = compDiv;
    } else {
      params.program = "Recreational";
    }
    try {
      const { data } = await api.get("/classes", { params });
      setClasses(data);
      try { sessionStorage.setItem(`sts:classes:${tab}:${compDiv}`, JSON.stringify(data)); } catch {}
    }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { loadSummary(); }, []);
  useEffect(() => {
    // Show cached instantly, refresh in the background
    const c = (() => { try { return JSON.parse(sessionStorage.getItem(`sts:classes:${tab}:${compDiv}`) || "null"); } catch { return null; } })();
    if (Array.isArray(c) && c.length) setClasses(c);
    loadClasses();
  }, [tab, compDiv]);

  // Deep-link: /classes?open=<class_id> auto-opens that class detail modal
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId || !classes.length) return;
    setSearchParams({}, { replace: true }); // open once; don't re-trigger on tab changes
    const cls = classes.find((c) => c.id === openId);
    if (cls) { openClass(cls); return; }
    // Class lives on the other program tab (e.g. Competitive): switch tabs, then open it
    const other = tab === "Competitive" ? "Recreational" : "Competitive";
    api.get("/classes", { params: { program: other } }).then(({ data }) => {
      const hit = (data || []).find((c) => c.id === openId);
      if (hit) { setCompDiv("All"); setTab(other); openClass(hit); }
    }).catch(() => {});
  }, [searchParams, classes]);

  const openClass = async (cls) => {
    setActive(cls);
    try { const { data } = await api.get(`/classes/${cls.id}/students`); setStudents(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const openAddStudent = async () => {
    setAddOpen(true);
    setAddForm({ mode: "existing", student_id: "", phone: "", student_name: "", parent_first_name: "" });
    setStudentSearch("");
    if (allStudents.length === 0) {
      try {
        const { data } = await api.get("/students");
        setAllStudents(data || []);
      } catch (e) { toast.error(formatApiError(e)); }
    }
  };

  const saveAddStudent = async () => {
    setAddSaving(true);
    try {
      let payload;
      if (addForm.mode === "existing") {
        if (!addForm.student_id) return toast.error("Pick a student");
        payload = { student_id: addForm.student_id };
      } else {
        if (!addForm.student_name.trim()) return toast.error("Student name required");
        if (!addForm.phone.trim()) return toast.error("Parent phone required");
        payload = {
          student_name: addForm.student_name.trim(),
          phone: addForm.phone.trim(),
          first_name: (addForm.parent_first_name || "").trim() || "Parent",
        };
      }
      const { data } = await api.post(`/classes/${active.id}/enroll`, payload);
      const displayName = addForm.mode === "existing"
        ? (allStudents.find(s => s.id === addForm.student_id)?.name || "Student")
        : addForm.student_name.trim();
      if (data.already_enrolled) toast.info(`${displayName} was already in this class`);
      else toast.success(`${displayName} added to ${active.name}`);
      setAddOpen(false);
      const { data: fresh } = await api.get(`/classes/${active.id}/students`);
      setStudents(fresh);
      loadClasses();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAddSaving(false); }
  };

  const removeStudent = async (enrollment_id, student_name) => {
    if (!confirm(`Remove ${student_name} from ${active.name}?`)) return;
    try {
      await api.delete(`/enrollments/${enrollment_id}`);
      toast.success(`${student_name} removed`);
      const { data } = await api.get(`/classes/${active.id}/students`);
      setStudents(data);
      loadClasses();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openTextForContact = async (contactId) => {
    if (!contactId) { toast.error("No phone on file to text"); return; }
    try {
      const { data } = await api.post("/conversations/by-contact", { contact_id: contactId });
      nav(`/inbox/${data.conversation_id}`);
    } catch (e) { toast.error(formatApiError(e, "Couldn't open thread")); }
  };

  const filteredStudents = (() => {
    const s = studentSearch.trim().toLowerCase();
    if (!s) return allStudents.slice(0, 50);
    return allStudents.filter(x => (x.name || "").toLowerCase().includes(s)).slice(0, 50);
  })();

  const quickBroadcast = async (cls) => {
    // Load enrolled students and jump straight to the broadcast composer
    setActive(cls);
    try {
      const { data } = await api.get(`/classes/${cls.id}/students`);
      setStudents(data);
      setOpenMsg(true);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const broadcastClass = async () => {
    if (!body.trim() || !active) return;
    setSending(true);
    try {
      const { data } = await api.post("/messages/broadcast", { class_id: active.id, body });
      toast.success(`Sent to ${data.sent_count} students`);
      setOpenMsg(false); setBody(""); setActive(null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const broadcastGroup = async () => {
    if (!body.trim() || !broadcastMode) return;
    setSending(true);
    try {
      const { data } = await api.post("/messages/broadcast-program", { program: broadcastMode.program, division: broadcastMode.division, body });
      toast.success(`Sent to ${data.sent_count} parents in ${broadcastMode.label}`);
      setBroadcastMode(null); setBody("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data: meta } = await api.post("/classes/sync");
      if (meta.synced) toast.success(`Synced ${meta.count} classes`);
      else toast.info(meta.reason || "Class metadata not configured");
      // Kick off background roster reconcile and poll until it completes.
      try {
        const { data: start } = await api.post("/jackrabbit/reconcile-rosters");
        toast.info(`Roster reconcile started (background)…`);
        const jobId = start.job_id;
        const started = Date.now();
        for (;;) {
          await new Promise((r) => setTimeout(r, 3000));
          const { data: job } = await api.get(`/jackrabbit/reconcile-rosters/${jobId}`);
          if (job.status === "completed") {
            const parts = [`scanned ${job.total_classes || 0}`];
            if (job.dropped_count) parts.push(`dropped ${job.dropped_count}`);
            if (job.restored_count) parts.push(`restored ${job.restored_count}`);
            if (job.added_count) parts.push(`added ${job.added_count} new students`);
            if (job.cleaned_count) parts.push(`removed ${job.cleaned_count} leftover students`);
            if (job.review_count) parts.push(`${job.review_count} need review (see Sync history)`);
            if (job.phones_updated_count) parts.push(`updated ${job.phones_updated_count} parent numbers`);
            if (job.family_fixes_count) parts.push(`fixed ${job.family_fixes_count} family links`);
            if (job.merged_count) parts.push(`merged ${job.merged_count} duplicates`);
            if (job.renamed_count) parts.push(`updated ${job.renamed_count} names`);
            if (job.missing_in_app_count) parts.push(`${job.missing_in_app_count} skipped (no phone)`);
            if (job.errored) parts.push(`${job.errored} errors`);
            toast.success(`Roster reconcile done · ${parts.join(" · ")}`);
            break;
          }
          if (Date.now() - started > 10 * 60 * 1000) {
            toast.message("Reconcile still running — check Classes again in a moment");
            break;
          }
        }
      } catch (e2) {
        toast.error(`Roster reconcile failed: ${formatApiError(e2)}`);
      }
      loadSummary(); loadClasses();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSyncing(false); }
  };

  const importRoster = async () => {
    if (!importText.trim()) { toast.error("Paste your CSV first"); return; }
    setImporting(true); setImportResult(null);
    try {
      const { data } = await api.post("/enrollments/import-csv", { csv_text: importText });
      setImportResult(data);
      toast.success(`Imported ${data.imported} enrollments (${data.skipped} skipped)`);
      loadSummary(); loadClasses();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setImporting(false); }
  };

  const onCsvFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const text = await f.text();
    setImportText(text);
    toast.info(`Loaded ${f.name} (${text.split("\n").length - 1} rows)`);
  };

  const onSyncFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const text = await f.text();
    setSyncText(text);
    setSyncFileName(f.name);
    setSyncPreview(null);
    toast.info(`Loaded ${f.name} (${text.split("\n").length - 1} rows)`);
  };

  const previewSync = async () => {
    if (!syncText.trim()) { toast.error("Upload the Student Listing CSV first"); return; }
    setSyncLoading(true); setSyncPreview(null);
    try {
      const { data } = await api.post("/roster/sync-student-listing", { csv_text: syncText, apply: false });
      setSyncPreview(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSyncLoading(false); }
  };

  const applySync = async () => {
    if (!syncText.trim() || !syncPreview) return;
    if (!window.confirm(`Apply changes?\n\n+ ${syncPreview.enroll_creates} new enrollments\n+ ${syncPreview.enroll_reactivates} reactivated\n- ${syncPreview.enroll_drops} dropped\n+ ${syncPreview.classes_to_create.length} new classes\n\nThis writes to the database.`)) return;
    setSyncApplying(true);
    try {
      const { data } = await api.post("/roster/sync-student-listing", { csv_text: syncText, apply: true });
      toast.success(`Roster synced — ${data.enroll_creates} new, ${data.enroll_drops} dropped, ${data.classes_created} classes added`);
      setSyncOpen(false); setSyncText(""); setSyncFileName(""); setSyncPreview(null);
      loadSummary(); loadClasses();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSyncApplying(false); }
  };

  const openEditClass = (cls) => {
    setEditForm({
      id: cls?.id || null,
      name: cls?.name || "",
      day: cls?.day || "",
      time: cls?.time || "",
      instructor: cls?.instructor || "",
      program: cls?.program || "Recreational",
      division: cls?.division || "",
      location_code: cls?.location_code || "",
      enrolled_count: cls?.enrolled_count || 0,
    });
    setEditOpen(true);
  };

  const saveEditClass = async () => {
    if (!editForm) return;
    const name = (editForm.name || "").trim();
    if (!name) { toast.error("Class name is required"); return; }
    setEditSaving(true);
    try {
      const payload = {
        name,
        day: editForm.day || "",
        time: editForm.time || "",
        instructor: editForm.instructor || "",
        program: editForm.program || "Recreational",
        division: editForm.division || null,
        location_code: editForm.location_code || "",
      };
      if (editForm.id) {
        await api.put(`/classes/${editForm.id}`, payload);
        toast.success(`Updated ${name}`);
      } else {
        await api.post("/classes", payload);
        toast.success(`Created ${name}`);
      }
      setEditOpen(false); setEditForm(null);
      loadSummary(); loadClasses();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setEditSaving(false); }
  };

  const deleteClass = async () => {
    if (!editForm?.id) return;
    if (editForm.enrolled_count > 0) {
      toast.error(`Cannot delete — ${editForm.enrolled_count} student(s) still enrolled. Drop them first.`);
      return;
    }
    if (!window.confirm(`Delete class "${editForm.name}"? This cannot be undone.`)) return;
    setEditSaving(true);
    try {
      await api.delete(`/classes/${editForm.id}`);
      toast.success(`Deleted ${editForm.name}`);
      setEditOpen(false); setEditForm(null);
      loadSummary(); loadClasses();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setEditSaving(false); }
  };

  const rec = summary?.Recreational;
  const comp = summary?.Competitive;
  const divs = comp?.divisions || {};

  return (
    <div className="h-full overflow-y-auto thin-scroll" data-testid="classes-page">
      <div className="max-w-6xl mx-auto p-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">Classes</h1>
            <p className="text-sm text-slate-500 mt-1">Recreational vs Competitive, split by division. Rosters sync from Jackrabbit.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={() => openEditClass(null)} className="bg-[#D9232E] hover:bg-[#B91C25] text-white" data-testid="new-class-button">
              <Plus className="w-4 h-4 mr-2" /> New class
            </Button>
            <Button variant="outline" onClick={() => { setSyncOpen(true); setSyncText(""); setSyncPreview(null); setSyncFileName(""); }} data-testid="sync-roster-button" className="border-[#D9232E]/40 text-[#D9232E] hover:bg-red-50">
              <Upload className="w-4 h-4 mr-2" /> Sync roster from CSV
            </Button>
            <Button variant="outline" onClick={() => { setImportOpen(true); setImportResult(null); }} data-testid="import-roster-button">
              <Upload className="w-4 h-4 mr-2" /> Import roster CSV
            </Button>
            <Button variant="outline" onClick={sync} disabled={syncing} data-testid="sync-classes-button">
              {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Sync from Jackrabbit
            </Button>
            <Button variant="outline" onClick={() => setHistoryOpen(true)} data-testid="sync-history-button">
              <Clock className="w-4 h-4 mr-2" /> Sync history
            </Button>
            <SyncHistoryDialog open={historyOpen} onOpenChange={setHistoryOpen} />
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mb-4">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="Recreational" data-testid="tab-recreational" className="data-[state=active]:bg-white data-[state=active]:text-[#D9232E]">
              <Sparkles className="w-4 h-4 mr-2" /> Recreational
              {rec && <span className="ml-2 text-xs text-slate-500">({rec.class_count})</span>}
            </TabsTrigger>
            <TabsTrigger value="Competitive" data-testid="tab-competitive" className="data-[state=active]:bg-white data-[state=active]:text-[#D9232E]">
              <Trophy className="w-4 h-4 mr-2" /> Competitive
              {comp && <span className="ml-2 text-xs text-slate-500">({comp.class_count})</span>}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="Recreational" className="mt-4">
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm text-slate-500">
                {rec ? `${rec.class_count} classes · ${rec.student_count} unique parents` : "..."}
              </div>
              <Button size="sm" onClick={() => setBroadcastMode({ program: "Recreational", division: null, label: "All Recreational", count: rec?.student_count || 0 })} disabled={!rec?.student_count} className="bg-[#D9232E] hover:bg-[#B91C25] text-white" data-testid="broadcast-rec-button">
                <Send className="w-3.5 h-3.5 mr-2" /> Text All Recreational
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="Competitive" className="mt-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                {["All", "Elite", "Company", "Other"].map((d) => {
                  const count = d === "All" ? comp?.class_count : divs[d]?.class_count;
                  return (
                    <Button
                      key={d}
                      size="sm"
                      variant={compDiv === d ? "default" : "outline"}
                      onClick={() => setCompDiv(d)}
                      className={compDiv === d ? "bg-[#D9232E] hover:bg-[#B91C25] text-white" : ""}
                      data-testid={`comp-div-${d}`}
                    >
                      {d} {count !== undefined && <span className="ml-1.5 text-[10px] opacity-70">({count})</span>}
                    </Button>
                  );
                })}
              </div>
              <Button
                size="sm"
                onClick={() => {
                  const label = compDiv === "All" ? "All Competitive" : `Competitive ${compDiv}`;
                  const count = compDiv === "All" ? comp?.student_count : divs[compDiv]?.student_count;
                  setBroadcastMode({ program: "Competitive", division: compDiv === "All" ? null : compDiv, label, count });
                }}
                disabled={compDiv === "All" ? !comp?.student_count : !divs[compDiv]?.student_count}
                className="bg-[#D9232E] hover:bg-[#B91C25] text-white"
                data-testid="broadcast-comp-button"
              >
                <Send className="w-3.5 h-3.5 mr-2" /> Text {compDiv === "All" ? "All Competitive" : compDiv}
              </Button>
            </div>
          </TabsContent>
        </Tabs>

        <div className="relative mb-2">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input
            placeholder="Search classes by name, instructor, day, or location..."
            value={classSearch}
            onChange={(e) => setClassSearch(e.target.value)}
            className="pl-9 bg-white border-slate-200"
            data-testid="classes-search-input"
          />
          {classSearch && (
            <button
              type="button"
              onClick={() => setClassSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#D9232E] p-1"
              data-testid="classes-search-clear"
              title="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-1 mb-4 flex-wrap">
          <span className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 mr-1">Day:</span>
          {["All","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setClassDay(d)}
              className={`px-2.5 py-1 rounded text-xs font-semibold border transition-colors ${classDay === d ? "bg-[#D9232E] text-white border-[#D9232E]" : "bg-white text-slate-600 border-slate-200 hover:border-[#D9232E] hover:text-[#D9232E]"}`}
              data-testid={`classes-day-${d.toLowerCase()}`}
            >
              {d === "All" ? "All Days" : d.slice(0,3)}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(() => {
            const q = classSearch.trim().toLowerCase();
            const filtered = classes.filter((c) => {
              if (classDay !== "All" && (c.day || "") !== classDay) return false;
              if (q) {
                const hay = `${c.name || ""} ${c.instructor || ""} ${c.day || ""} ${c.time || ""} ${c.location_code || ""} ${c.division || ""}`.toLowerCase();
                if (!hay.includes(q)) return false;
              }
              return true;
            });
            if (filtered.length === 0) {
              return <div className="col-span-full text-center py-12 text-slate-400 text-sm">
                {q || classDay !== "All" ? "No classes match your filters" : "No classes in this group"}
              </div>;
            }
            return filtered.map((cls) => <ClassCard key={cls.id} cls={cls} onOpen={openClass} onBroadcast={quickBroadcast} onEdit={openEditClass} />);
          })()}
        </div>

        {/* Class detail dialog */}
        <Dialog open={!!active && !openMsg} onOpenChange={(v) => { if (!v) setActive(null); }}>
          <DialogContent className="max-w-2xl" data-testid="class-detail-dialog">
            <DialogHeader>
              <DialogTitle>{active?.name}</DialogTitle>
              <p className="text-sm text-slate-500">{active?.instructor} &middot; {active?.day}s at {active?.time}</p>
            </DialogHeader>
            <div className="max-h-96 overflow-y-auto thin-scroll border border-slate-200 rounded-lg" data-testid="students-list">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide sticky top-0">
                  <tr><th className="text-left px-4 py-2 font-semibold">Student</th><th className="text-left px-4 py-2 font-semibold">Phone</th><th className="w-10"></th></tr>
                </thead>
                <tbody>
                  {students.length === 0 && <tr><td colSpan={3} className="text-center py-8 text-slate-400">No students enrolled</td></tr>}
                  {students.map((s) => (
                    <tr key={s.enrollment_id} className="border-t border-slate-100 group">
                      <td className="px-4 py-2.5 font-medium text-slate-900">
                        {s.contact_id ? (
                          <button
                            type="button"
                            onClick={() => openTextForContact(s.contact_id)}
                            className="text-slate-900 hover:text-[#D9232E] hover:underline text-left"
                            data-testid={`open-student-${s.student_id || s.enrollment_id}`}
                            title={`Open chat with ${s.student_name}${s.phone ? ` (${s.phone})` : ""}`}
                          >
                            {s.student_name}
                          </button>
                        ) : s.student_name}
                      </td>
                      <td className="px-4 py-2.5 text-slate-600">{s.phone}</td>
                      <td className="px-2 py-2">
                        <div className="flex items-center gap-1 justify-end">
                          {s.contact_id && (
                            <Button size="icon" variant="ghost" onClick={() => openTextForContact(s.contact_id)} className="h-7 w-7 opacity-40 group-hover:opacity-100 transition-opacity hover:bg-red-50 hover:text-[#D9232E]" data-testid={`text-student-${s.enrollment_id}`} title={`Text ${s.student_name}`}>
                              <MessageSquareText className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          <Button size="icon" variant="ghost" onClick={() => removeStudent(s.enrollment_id, s.student_name)} className="h-7 w-7 opacity-40 group-hover:opacity-100 transition-opacity hover:bg-red-50 hover:text-[#D9232E]" data-testid={`remove-student-${s.enrollment_id}`} title="Drop from class">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <DialogFooter className="justify-between sm:justify-between">
              <Button variant="outline" onClick={openAddStudent} data-testid="add-student-button">
                <UserPlus className="w-4 h-4 mr-2" /> Add student
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setActive(null)}>Close</Button>
                <Button className="bg-[#D9232E] hover:bg-[#B91C25] text-white" onClick={() => setOpenMsg(true)} disabled={students.length === 0} data-testid="message-class-button">
                  <Send className="w-4 h-4 mr-2" /> Message Class ({students.length})
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Class broadcast */}
        <Dialog open={openMsg} onOpenChange={(v) => { setOpenMsg(v); if (!v) setBody(""); }}>
          <DialogContent data-testid="broadcast-dialog">
            <DialogHeader>
              <DialogTitle>Text {active?.name}</DialogTitle>
              <p className="text-sm text-slate-500">Individual SMS to each parent. Replies come back individually.</p>
            </DialogHeader>
            <div className="flex items-center justify-between">
              <div className="text-xs text-slate-400">{body.length} chars &middot; {students.length} recipients</div>
              <TemplatePicker onSelect={(b) => setBody(b)} testId="class-broadcast-template-picker" />
            </div>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Type your message..." rows={5} data-testid="broadcast-body" />
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpenMsg(false)}>Cancel</Button>
              <Button onClick={broadcastClass} disabled={sending || !body.trim()} className="bg-[#D9232E] hover:bg-[#B91C25] text-white" data-testid="broadcast-send-button">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : `Send to ${students.length}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Program/Division broadcast */}
        <Dialog open={!!broadcastMode} onOpenChange={(v) => { if (!v) { setBroadcastMode(null); setBody(""); } }}>
          <DialogContent data-testid="program-broadcast-dialog">
            <DialogHeader>
              <DialogTitle>Text {broadcastMode?.label}</DialogTitle>
              <p className="text-sm text-slate-500">Individual SMS to {broadcastMode?.count} unique parents.</p>
            </DialogHeader>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <SegmentMeter body={body} recipients={broadcastMode?.count || 0} variant="broadcast" onFix={setBody} testId="program-broadcast-segment-meter" />
              <TemplatePicker onSelect={(b) => setBody(b)} testId="program-broadcast-template-picker" />
            </div>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Type your message..." rows={5} data-testid="program-broadcast-body" />
            <DialogFooter>
              <Button variant="outline" onClick={() => setBroadcastMode(null)}>Cancel</Button>
              <Button onClick={broadcastGroup} disabled={sending || !body.trim()} className="bg-[#D9232E] hover:bg-[#B91C25] text-white" data-testid="program-broadcast-send">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : `Send to ${broadcastMode?.count}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add student manually */}
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogContent data-testid="add-student-dialog">
            <DialogHeader>
              <DialogTitle>Add student to {active?.name}</DialogTitle>
              <p className="text-sm text-slate-500">Search an existing student or add a brand new one.</p>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex gap-1 p-1 bg-slate-100 rounded-md">
                <button
                  type="button"
                  onClick={() => setAddForm({ ...addForm, mode: "existing" })}
                  className={`flex-1 px-3 py-1.5 rounded text-xs font-semibold transition-colors ${addForm.mode === "existing" ? "bg-white text-[#D9232E] shadow-sm" : "text-slate-600"}`}
                  data-testid="add-mode-existing"
                >Existing student</button>
                <button
                  type="button"
                  onClick={() => setAddForm({ ...addForm, mode: "new" })}
                  className={`flex-1 px-3 py-1.5 rounded text-xs font-semibold transition-colors ${addForm.mode === "new" ? "bg-white text-[#D9232E] shadow-sm" : "text-slate-600"}`}
                  data-testid="add-mode-new"
                >New student</button>
              </div>
              {addForm.mode === "existing" ? (
                <div>
                  <label className="text-xs font-semibold text-slate-700 mb-1 block">Search student</label>
                  <Textarea
                    rows={1}
                    value={studentSearch}
                    onChange={(e) => setStudentSearch(e.target.value.replace(/\n/g,''))}
                    placeholder="Type a student's name..."
                    className="resize-none"
                    data-testid="student-search-input"
                  />
                  <div className="mt-2 max-h-56 overflow-y-auto border border-slate-200 rounded-md bg-white">
                    {filteredStudents.length === 0 ? (
                      <div className="p-4 text-center text-xs text-slate-400">No matches. Try a different name or switch to "New student".</div>
                    ) : filteredStudents.map(s => {
                      const active = s.active_classes?.length || 0;
                      return (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => setAddForm({ ...addForm, student_id: s.id })}
                          className={`w-full text-left px-3 py-2 border-b border-slate-100 text-sm transition-colors ${addForm.student_id === s.id ? "bg-red-50" : "hover:bg-slate-50"}`}
                          data-testid={`pick-student-${s.id}`}
                        >
                          <div className="font-medium text-slate-900">{s.name}</div>
                          <div className="text-[11px] text-slate-500">{active} active class{active === 1 ? "" : "es"}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-700 mb-1 block">Student name *</label>
                    <Textarea rows={1} value={addForm.student_name} onChange={(e) => setAddForm({ ...addForm, student_name: e.target.value.replace(/\n/g,'') })} placeholder="First Last" className="resize-none" data-testid="new-student-name" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-700 mb-1 block">Parent phone *</label>
                    <Textarea rows={1} value={addForm.phone} onChange={(e) => setAddForm({ ...addForm, phone: e.target.value.replace(/\n/g,'') })} placeholder="+13615551234" className="resize-none" data-testid="new-parent-phone" />
                    <p className="text-[10px] text-slate-400 mt-1">Only used to text this family — parent name isn't shown anywhere.</p>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button onClick={saveAddStudent} disabled={addSaving} className="bg-[#D9232E] hover:bg-[#B91C25] text-white" data-testid="save-add-student">
                {addSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add to class"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit / Create Class Dialog */}
        <Dialog open={editOpen} onOpenChange={(v) => { setEditOpen(v); if (!v) setEditForm(null); }}>
          <DialogContent className="max-w-lg" data-testid="edit-class-dialog">
            <DialogHeader>
              <DialogTitle>{editForm?.id ? "Edit class" : "New class"}</DialogTitle>
              <p className="text-sm text-slate-500">Set the class details. Only the name is required.</p>
            </DialogHeader>
            {editForm && (
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Class name*</label>
                  <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-class-name" className="mt-1" />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Day</label>
                  <select value={editForm.day} onChange={(e) => setEditForm({ ...editForm, day: e.target.value })} className="mt-1 w-full h-10 border border-slate-200 rounded-md px-3 text-sm" data-testid="edit-class-day">
                    <option value="">—</option>
                    {["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"].map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Time</label>
                  <Input value={editForm.time} onChange={(e) => setEditForm({ ...editForm, time: e.target.value })} placeholder="e.g. 4:15 PM" data-testid="edit-class-time" className="mt-1" />
                </div>
                <div className="col-span-2">
                  <label className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Instructor</label>
                  <Input value={editForm.instructor} onChange={(e) => setEditForm({ ...editForm, instructor: e.target.value })} data-testid="edit-class-instructor" className="mt-1" />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Program</label>
                  <select value={editForm.program} onChange={(e) => setEditForm({ ...editForm, program: e.target.value })} className="mt-1 w-full h-10 border border-slate-200 rounded-md px-3 text-sm" data-testid="edit-class-program">
                    <option value="Recreational">Recreational</option>
                    <option value="Competitive">Competitive</option>
                  </select>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Division</label>
                  <select value={editForm.division || ""} onChange={(e) => setEditForm({ ...editForm, division: e.target.value })} className="mt-1 w-full h-10 border border-slate-200 rounded-md px-3 text-sm" data-testid="edit-class-division">
                    <option value="">—</option>
                    <option value="Elite">Elite</option>
                    <option value="Company">Company</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">Location</label>
                  <Input value={editForm.location_code} onChange={(e) => setEditForm({ ...editForm, location_code: e.target.value })} placeholder="e.g. Victoria, El Campo, Palacios" data-testid="edit-class-location" className="mt-1" />
                </div>
                {editForm.id && (
                  <div className="col-span-2 text-[11px] text-slate-500">
                    Currently enrolled: <b>{editForm.enrolled_count}</b> student(s). {editForm.enrolled_count > 0 && "Drop them before deleting the class."}
                  </div>
                )}
              </div>
            )}
            <DialogFooter className="flex-wrap gap-2">
              {editForm?.id && (
                <Button variant="outline" onClick={deleteClass} disabled={editSaving || (editForm?.enrolled_count || 0) > 0} className="text-[#D9232E] border-[#D9232E]/40 hover:bg-red-50 mr-auto" data-testid="delete-class-button">
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
              )}
              <Button variant="ghost" onClick={() => { setEditOpen(false); setEditForm(null); }}>Cancel</Button>
              <Button onClick={saveEditClass} disabled={editSaving || !editForm?.name?.trim()} className="bg-[#D9232E] hover:bg-[#B91C25] text-white" data-testid="save-class-button">
                {editSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editForm?.id ? "Save changes" : "Create class")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* One-Button Student Listing Sync */}
        <Dialog open={syncOpen} onOpenChange={(v) => { setSyncOpen(v); if (!v) { setSyncText(""); setSyncFileName(""); setSyncPreview(null); } }}>
          <DialogContent className="max-w-3xl" data-testid="sync-roster-dialog">
            <DialogHeader>
              <DialogTitle>Sync roster from Jackrabbit Student Listing</DialogTitle>
              <p className="text-sm text-slate-500">Upload your latest Student Listing CSV → preview the diff → apply. Auto-adds missing kids, drops transfers, and creates any missing classes.</p>
            </DialogHeader>
            <div className="space-y-3">
              <div className="text-xs text-slate-600 bg-red-50 border border-red-200 rounded-md p-3 leading-relaxed">
                <div className="font-semibold text-slate-900 mb-1">Required columns:</div>
                <code className="text-[11px] block">Loc, Student, Current Classes</code>
                <div className="text-slate-500 mt-2">In Jackrabbit: <b>Reports → Student Listing → Export CSV</b>. Class names ending in ", TX" are handled automatically.</div>
              </div>
              <div className="flex items-center gap-3">
                <input type="file" accept=".csv" onChange={onSyncFile} className="text-xs" data-testid="sync-roster-file-input" />
                {syncFileName && <span className="text-xs text-slate-600">{syncFileName}</span>}
              </div>
              {syncPreview && (
                <div className="rounded-md border border-slate-200 bg-white p-4 text-xs space-y-2" data-testid="sync-preview">
                  <div className="font-semibold text-slate-900 mb-1">Preview — nothing written yet</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center">
                    <div className="rounded bg-emerald-50 border border-emerald-200 p-2">
                      <div className="text-lg font-bold text-emerald-700">{syncPreview.enroll_creates}</div>
                      <div className="text-emerald-600 text-[10px] uppercase">New enrollments</div>
                    </div>
                    <div className="rounded bg-blue-50 border border-blue-200 p-2">
                      <div className="text-lg font-bold text-blue-700">{syncPreview.enroll_reactivates}</div>
                      <div className="text-blue-600 text-[10px] uppercase">Reactivated</div>
                    </div>
                    <div className="rounded bg-slate-50 border border-slate-200 p-2">
                      <div className="text-lg font-bold text-slate-700">{syncPreview.enroll_drops}</div>
                      <div className="text-slate-500 text-[10px] uppercase">Dropped</div>
                    </div>
                    <div className="rounded bg-amber-50 border border-amber-200 p-2">
                      <div className="text-lg font-bold text-amber-700">{syncPreview.classes_to_create.length}</div>
                      <div className="text-amber-700 text-[10px] uppercase">New classes</div>
                    </div>
                  </div>
                  {syncPreview.classes_to_create.length > 0 && (
                    <details className="pt-2 border-t border-slate-100">
                      <summary className="cursor-pointer text-slate-600 hover:text-slate-900 font-semibold">Classes that will be auto-created ({syncPreview.classes_to_create.length})</summary>
                      <ul className="mt-2 space-y-0.5 max-h-40 overflow-y-auto pl-3">
                        {syncPreview.classes_to_create.map((n, i) => <li key={i} className="text-slate-600">• {n}</li>)}
                      </ul>
                    </details>
                  )}
                  {syncPreview.unmatched_students.length > 0 && (
                    <details className="pt-2 border-t border-slate-100">
                      <summary className="cursor-pointer text-amber-700 font-semibold">⚠ CSV students not found in app ({syncPreview.unmatched_students.length}) — will be skipped</summary>
                      <ul className="mt-2 space-y-0.5 max-h-40 overflow-y-auto pl-3">
                        {syncPreview.unmatched_students.map((n, i) => <li key={i} className="text-amber-700">• {n}</li>)}
                      </ul>
                      <div className="mt-2 text-slate-500 text-[11px]">Add these families first via Contacts, then re-run.</div>
                    </details>
                  )}
                  {syncPreview.sample_changes?.length > 0 && (
                    <details className="pt-2 border-t border-slate-100">
                      <summary className="cursor-pointer text-slate-600 hover:text-slate-900 font-semibold">Sample changes (first {syncPreview.sample_changes.length})</summary>
                      <ul className="mt-2 space-y-1.5 max-h-56 overflow-y-auto pl-3">
                        {syncPreview.sample_changes.map((d, i) => (
                          <li key={i} className="text-slate-700">
                            <div className="font-semibold">{d.student_name}</div>
                            {d.add?.length > 0 && <div className="text-emerald-700 pl-2">+ {d.add.join(", ")}</div>}
                            {d.reactivate?.length > 0 && <div className="text-blue-700 pl-2">↺ {d.reactivate.join(", ")}</div>}
                            {d.drop?.length > 0 && <div className="text-slate-500 line-through pl-2">- {d.drop.join(", ")}</div>}
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSyncOpen(false)}>Cancel</Button>
              {!syncPreview ? (
                <Button onClick={previewSync} disabled={syncLoading || !syncText.trim()} className="bg-slate-800 hover:bg-slate-900 text-white" data-testid="sync-roster-preview">
                  {syncLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Preview changes"}
                </Button>
              ) : (
                <>
                  <Button variant="ghost" onClick={() => setSyncPreview(null)} data-testid="sync-roster-back">Back</Button>
                  <Button onClick={applySync} disabled={syncApplying} className="bg-[#D9232E] hover:bg-[#B91C25] text-white" data-testid="sync-roster-apply">
                    {syncApplying ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Applying...</> : "Apply changes"}
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* CSV Roster Import */}
        <Dialog open={importOpen} onOpenChange={(v) => { setImportOpen(v); if (!v) { setImportText(""); setImportResult(null); } }}>          <DialogContent className="max-w-2xl" data-testid="import-csv-dialog">
            <DialogHeader>
              <DialogTitle>Bulk import class rosters</DialogTitle>
              <p className="text-sm text-slate-500">Paste a Jackrabbit-style CSV, or upload a .csv file. Each row = one enrollment.</p>
            </DialogHeader>
            <div className="space-y-3">
              <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md p-3 leading-relaxed">
                <div className="font-semibold text-slate-900 mb-1">Required column headers (any order, any case):</div>
                <code className="text-[11px] block">class_name, phone, student_name, first_name, last_name</code>
                <div className="text-slate-500 mt-2">Optional: <code>email, program, jackrabbit_student_id</code>. <b>Class names</b> must match your existing classes (fuzzy match). In Jackrabbit: <b>Reports → Students by Class → Export CSV</b>.</div>
              </div>
              <div className="flex items-center gap-2">
                <input type="file" accept=".csv" onChange={onCsvFile} className="text-xs" data-testid="import-csv-file-input" />
                <span className="text-xs text-slate-400">or paste below</span>
              </div>
              <Textarea
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
                placeholder={"class_name,student_name,first_name,last_name,phone,email\nCC: Tutu Ballet & Tap,Emma Wilson,Sarah,Wilson,+16045550101,sarah@example.com"}
                rows={8}
                className="font-mono text-[11px]"
                data-testid="import-csv-textarea"
              />
              {importResult && (
                <div className="rounded-md border border-slate-200 bg-white p-3 text-xs" data-testid="import-result">
                  <div className="font-semibold text-slate-900 mb-2">Import complete</div>
                  <div className="flex gap-4 mb-2">
                    <span className="text-emerald-700">✅ {importResult.imported} imported</span>
                    <span className="text-slate-500">⏭ {importResult.skipped} skipped</span>
                  </div>
                  {importResult.errors?.length > 0 && (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-slate-600 hover:text-slate-900">Show issues ({importResult.errors.length})</summary>
                      <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                        {importResult.errors.map((er, idx) => (
                          <li key={idx} className="text-slate-500">Row {er.row}: {er.reason}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setImportOpen(false)}>Close</Button>
              <Button onClick={importRoster} disabled={importing || !importText.trim()} className="bg-[#D9232E] hover:bg-[#B91C25] text-white" data-testid="import-csv-run-button">
                {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Import"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
